import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Menu, ChevronDown, Car, Armchair, Footprints, Warehouse, Stamp, Palette, ShieldCheck, Star, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { COMPANY, SERVICES } from "@/lib/constants";

const navLinks = [
  { to: "/pattern-studio", label: "Stamp Studio" },
  { to: "/gallery", label: "Gallery" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

const SERVICE_ICONS = {
  driveways: Car,
  patios: Armchair,
  sidewalks: Footprints,
  floors: Warehouse,
  "stamped-concrete": Stamp,
  "colored-concrete": Palette,
};

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Announcement bar — tucks away on scroll */}
      <div className={`bg-secondary text-white overflow-hidden transition-all duration-300 ${scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"}`} data-testid="announcement-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-center sm:justify-between text-xs font-semibold">
          <div className="hidden sm:flex items-center gap-5 text-white/70">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Licensed &amp; Insured</span>
            <span className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-primary" /> Free Estimates</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> Serving 25+ WI Communities</span>
          </div>
          <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <Phone className="h-3.5 w-3.5 text-primary" /> Call for a Free Quote: {COMPANY.phone}
          </a>
        </div>
      </div>

      {/* Main bar */}
      <div className={`bg-card/95 backdrop-blur border-b border-border transition-shadow ${scrolled ? "shadow-[0_6px_24px_rgba(0,0,0,0.08)]" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
            <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
              <img src="/archive/logo.jpg" alt="CG Concrete LLC" className={`object-contain rounded-sm transition-all duration-300 group-hover:scale-105 ${scrolled ? "h-11 w-11" : "h-14 w-14"}`} />
              <div className="block leading-none">
                <div className="font-heading font-extrabold text-base sm:text-lg tracking-tight">CG CONCRETE</div>
                <div className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] text-muted-foreground font-semibold">WATERTOWN, WI</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {/* Services mega-menu */}
              <div className="relative group">
                <button className="px-4 py-2 text-sm font-semibold flex items-center gap-1 hover:text-primary transition-colors" data-testid="nav-services">
                  Services <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                </button>
                <div className="absolute top-full left-0 pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                  <div className="bg-card border border-border shadow-2xl w-[620px] overflow-hidden">
                    <div className="grid grid-cols-2 gap-1 p-3">
                      {SERVICES.map((s) => {
                        const Icon = SERVICE_ICONS[s.slug] || Stamp;
                        return (
                          <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-start gap-3 p-3 hover:bg-muted transition-colors group/item" data-testid={`nav-service-${s.slug}`}>
                            <span className="h-10 w-10 shrink-0 bg-primary/10 flex items-center justify-center group-hover/item:bg-primary transition-colors">
                              <Icon className="h-5 w-5 text-primary group-hover/item:text-white transition-colors" />
                            </span>
                            <span className="min-w-0">
                              <span className="block font-bold text-sm">{s.title}</span>
                              <span className="block text-xs text-muted-foreground line-clamp-2 mt-0.5">{s.short}</span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                    <Link to="/pattern-studio" className="flex items-center justify-between bg-secondary text-white px-5 py-3.5 hover:bg-secondary/90 transition-colors" data-testid="megamenu-cta">
                      <span className="text-sm font-bold">Explore stamped colors &amp; patterns in the Stamp Studio</span>
                      <ArrowRight className="h-4 w-4 text-primary" />
                    </Link>
                  </div>
                </div>
              </div>

              {navLinks.map((l) => (
                <NavLink key={l.to} to={l.to} className={({ isActive }) => `relative px-4 py-2 text-sm font-semibold transition-colors hover:text-primary after:absolute after:left-4 after:right-4 after:-bottom-0.5 after:h-0.5 after:bg-primary after:transition-transform after:origin-left ${isActive ? "text-primary after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"}`} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <Button onClick={() => navigate("/contact")} className="hidden sm:inline-flex bg-primary hover:bg-primary/90 rounded-sm font-bold hover:-translate-y-0.5 transition-transform" data-testid="header-quote-btn">
                Free Quote
              </Button>
              <Link to="/login" className="hidden lg:inline-flex text-sm font-semibold px-3 py-2 hover:text-primary transition-colors" data-testid="header-login">
                Login
              </Link>

              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="lg:hidden rounded-sm" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 overflow-y-auto">
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <div className="flex flex-col gap-1 mt-8 pb-28">
                    {SERVICES.map((s) => {
                      const Icon = SERVICE_ICONS[s.slug] || Stamp;
                      return (
                        <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 font-semibold border-b border-border hover:text-primary" data-testid={`mobile-service-${s.slug}`}>
                          <Icon className="h-4 w-4 text-primary" /> {s.title}
                        </Link>
                      );
                    })}
                    {navLinks.map((l) => (
                      <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="px-3 py-3 font-semibold border-b border-border hover:text-primary" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                        {l.label}
                      </Link>
                    ))}
                    <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-3 font-semibold border-b border-border hover:text-primary">
                      Client Login
                    </Link>
                    <Button onClick={() => { setOpen(false); navigate("/contact"); }} className="mt-4 bg-primary rounded-sm font-bold">
                      Get Free Quote
                    </Button>
                    <a href={`tel:${COMPANY.phoneRaw}`} className="mt-2 text-center font-bold flex items-center justify-center gap-2">
                      <Phone className="h-4 w-4 text-primary" /> {COMPANY.phone}
                    </a>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
